import { Fragment, useEffect } from 'react';

import {
  ChartPieIcon,
  MessageSquareTextIcon,
  Share2Icon,
  UserRoundPlusIcon,
  WandSparklesIcon,
} from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import Logo from '@/components/atoms/Icons/Logo';
import { MessageQuestionIcon } from '@/components/atoms/Icons/MessageQuestionIcon';
import Loading from '@/components/atoms/Loading/Loading';
import { MessagesCircleIcon } from '@/components/icons/MessagesCircleIcon';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useAccount } from '@/hooks/useAccount';
import useMembers from '@/hooks/useMembers';
import useUser from '@/hooks/useUser';
import { setAccountSlug } from '@/redux/session/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { ADMIN, BUILDER, CHAT_AGENT, CHAT_MANAGER } from '@/util/constants';

import { RoleAction } from '../../components/RoleAction/RoleAction';
import { RoleBadge } from '../../components/RoleBadge/RoleBadge';

// This function is used to remove overlapping roles
// e.g. if a user has both CHAT_AGENT and CHAT_MANAGER roles, we only show CHAT_MANAGER
// This is because CHAT_MANAGER has more permissions than CHAT_AGENT
function getRoles(roles: string[]) {
  const finalRoles = [];
  const hasAdminRole = roles?.some((role) => role.includes(ADMIN));
  const hasBuilderRole = roles?.some((role) => role.includes(BUILDER));
  const hasChatAgentRole = roles?.some((role) => role.includes(CHAT_AGENT));
  const hasChatManagerRole = roles?.some((role) => role.includes(CHAT_MANAGER));

  if (hasAdminRole) {
    finalRoles.push(ADMIN);
    return finalRoles;
  }
  if (hasBuilderRole) {
    finalRoles.push(BUILDER);
  }
  if (hasChatAgentRole && !hasChatManagerRole) {
    finalRoles.push(CHAT_AGENT);
  }
  if (hasChatManagerRole) {
    finalRoles.push(CHAT_MANAGER);
  }

  return finalRoles;
}

type InnerRolesProps = { roles: string[] };

const InnerRoles = ({ roles }: InnerRolesProps) => {
  const { t } = useTranslation();

  return roles.map((role, index: number) => (
    <Fragment key={role}>
      <RoleBadge role={role} />
      {index < roles.length - 1 && <span> {t('rules.and')} </span>}
    </Fragment>
  ));
};

const ICON_PROPS = { size: 20, className: 'text-foreground-muted' } as const;

// This object defines the actions that a user can take based
//  on their roles and the icons that represent them
const ROLE_ACTIONS = {
  [ADMIN]: [
    { translation: 'build_your_virtual_agent', icon: <WandSparklesIcon {...ICON_PROPS} /> },
    { translation: 'manage_your_team', icon: <UserRoundPlusIcon {...ICON_PROPS} /> },
    { translation: 'monitor_success', icon: <ChartPieIcon {...ICON_PROPS} /> },
  ],
  [BUILDER]: [
    { translation: 'build', icon: <WandSparklesIcon {...ICON_PROPS} /> },
    { translation: 'test', icon: <MessageSquareTextIcon {...ICON_PROPS} /> },
    { translation: 'optimize', icon: <ChartPieIcon {...ICON_PROPS} /> },
  ],
  [CHAT_AGENT]: [
    { translation: 'create_quick_responses', icon: <MessageQuestionIcon {...ICON_PROPS} /> },
    { translation: 'auto_route_conversations', icon: <Share2Icon {...ICON_PROPS} /> },
    { translation: 'collaborate', icon: <MessagesCircleIcon {...ICON_PROPS} /> },
  ],
  [CHAT_MANAGER]: [
    { translation: 'manage_your_team', icon: <UserRoundPlusIcon {...ICON_PROPS} /> },
    { translation: 'create_quick_responses', icon: <MessageQuestionIcon {...ICON_PROPS} /> },
    { translation: 'monitor_performance', icon: <ChartPieIcon {...ICON_PROPS} /> },
  ],
} as const;

export const WelcomePage = () => {
  // Custom hooks
  const navigate = useNavigate();
  const { updateUserAsync, user } = useUser();
  const { account } = useAccount();
  const { member } = useMembers();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Redux selectors
  const accountSlug = useSelector(selectAccountSlug);

  // Set the account slug if not already set
  useEffect(() => {
    const slug = user?.accounts?.[0]?.slug;

    if (!accountSlug && slug) {
      dispatch(setAccountSlug(slug));
    }
  }, [accountSlug, user?.accounts, dispatch]);

  // Check if user data is loaded
  if (!user?.accounts || user.accounts.length === 0) {
    return <Loading />;
  }

  // Wait for accountSlug to be set and data to load
  if (!accountSlug || !account || !member) {
    return <Loading />;
  }

  // Local variables
  // User has already accepted invitation - get role from member data
  const role_ids = member?.role_ids || [];
  const roles = getRoles(role_ids);
  let roleActions = [];
  roles.forEach((role) => {
    roleActions = [...roleActions, ...ROLE_ACTIONS[role]];
  });
  const redirectToHome = role_ids?.includes(ADMIN) || role_ids?.includes(BUILDER);
  const redirectUrl = redirectToHome ? `/${accountSlug}` : `/${accountSlug}/chats`;

  // Callbacks
  const handleClick = async () => {
    try {
      // Clear the welcome flag using async/await
      const metadata = { ...user?.metadata };
      delete metadata.show_welcome;
      await updateUserAsync({ metadata });
      // Navigate after successful update
      navigate(redirectUrl, { replace: true });
    } catch (error) {
      console.error('Failed to clear welcome flag:', error);
      // Navigate anyway to avoid blocking the user
      navigate(redirectUrl, { replace: true });
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-2xl">
        <div className="mb-6 text-center">
          <Logo height={24} width={144} />
        </div>
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-semibold">
              <Trans i18nKey={'member_invitation.welcome_aboard'} values={{ 0: user?.name }} />
            </CardTitle>
            {roles.length > 0 && (
              <CardDescription className="mt-2 text-base">
                {t('member_invitation.as')} <InnerRoles roles={roles} />{' '}
                {t('member_invitation.you_can_do')}
              </CardDescription>
            )}
            {roles.length === 0 && (
              <CardDescription className="mt-2 text-base">
                {t('member_invitation.you_can_do')}
              </CardDescription>
            )}
          </CardHeader>

          {roleActions.length > 0 && (
            <CardContent className="space-y-4">
              <div className="grid gap-3">
                {roleActions.map((roleAction) => (
                  <RoleAction key={roleAction.translation} {...roleAction} />
                ))}
              </div>
            </CardContent>
          )}

          <CardFooter>
            <Button onClick={handleClick} className="w-full">
              {t('member_invitation.lets_start')}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};
